import { AxiosInstance, AxiosResponse } from 'axios'
import { DeleteUrls, GetUrls, CustomConfig, PatchUrls, PostUrls, PutUrls, RequestArgumentsAutoOrg, RequestResponse } from '../types/types'
import { ORGANIZATION_ID } from './enums'
import { showResponseNotifications } from './helper'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const fullFillURL = (urlTemplate: string, params: any, autoInjectOrgId = false) => {
	const pathParams = []
	const queryParams = { ...(params?.query || {}) }

	let pathParamsWithOrg = params?.path || {}
	let template = urlTemplate

	if (autoInjectOrgId) {
		// GetUrls keys are `/api/b2c/...` → `/api/{organizationID}/b2c/...`
		const pathAfterApi = urlTemplate.replace(/^\/api\//, '/')
		template = `/api/{organizationID}${pathAfterApi}`
		pathParamsWithOrg = { ...pathParamsWithOrg, organizationID: ORGANIZATION_ID.NOTINO }
	}

	const fullfilURL = template
		.split('/')
		.map((blok) => {
			if (/{[^}]*\}/.test(blok)) {
				const param = blok.replace('{', '').replace('}', '')
				pathParams.push(param)
				delete queryParams[param]
				return pathParamsWithOrg[param]
			}
			return blok
		})
		.join('/')
	return {
		fullfilURL,
		queryParams
	}
}

type HttpAbortRequest = 'httpGet' | 'httpPost' | 'httpPut' | 'httpPatch' | 'httpDelete'

/**
 * @type {Map<HttpAbortRequest, Map<string, AbortController>>} abort controllers for http requests
 */
export const abortControllers: Record<HttpAbortRequest, Map<string, AbortController>> = {
	httpGet: new Map<string, AbortController>(),
	httpPost: new Map<string, AbortController>(),
	httpPut: new Map<string, AbortController>(),
	httpPatch: new Map<string, AbortController>(),
	httpDelete: new Map<string, AbortController>()
}

export enum CANCEL_TOKEN_MESSAGES {
	CANCELED_DUE_TO_NEW_REQUEST = 'Operation canceled due to new request.'
}

/**
 * Builds an abort controller if allowAbort is true, and aborts the previous request if the abortControllerKey exists in the abortControllers map.
 *
 * @param {HttpAbortRequest} requestType - the type of the HTTP request
 * @param {string} abortControllerKey - the key used to identify the abort controller
 * @param {boolean} allowAbort - flag indicating whether to allow aborting
 * @return {object} an object with the signal property pointing to the abort controller's signal, if allowAbort is true; otherwise, an empty object
 */
const buildAbortController = (requestType: HttpAbortRequest, abortControllerKey: string, allowAbort?: boolean) => {
	if (!allowAbort) {
		return undefined
	}

	if (abortControllers[requestType].has(abortControllerKey)) {
		abortControllers[requestType].get(abortControllerKey)?.abort(CANCEL_TOKEN_MESSAGES.CANCELED_DUE_TO_NEW_REQUEST)
	}

	abortControllers[requestType].set(abortControllerKey, new AbortController())

	return abortControllers[requestType].get(abortControllerKey)?.signal
}

const DEFAULT_CUSTOM_CONFIG: Pick<CustomConfig, 'displayNotification' | 'abortSignalKey' | 'headers' | 'autoInjectOrganizationID'> = {
	displayNotification: true,
	abortSignalKey: '',
	headers: {},
	autoInjectOrganizationID: true
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const prepareRequestConfig = (requestType: HttpAbortRequest, urlTemplate: string, params: any, customConfig?: CustomConfig) => {
	const config = { ...DEFAULT_CUSTOM_CONFIG, ...customConfig }
	const { abortSignalKey, allowAbort, headers, autoInjectOrganizationID } = config

	const { fullfilURL, queryParams } = fullFillURL(urlTemplate, params, autoInjectOrganizationID)

	const signal = buildAbortController(requestType, abortSignalKey || fullfilURL, allowAbort)

	const requestConfig: CustomConfig = {
		...config,
		signal,
		headers
	}

	if (queryParams) {
		requestConfig.params = queryParams
	}

	return {
		requestConfig,
		fullfilURL
	}
}

const EMPTY_ARGS = {
	params: undefined,
	customConfig: undefined,
	reqBody: undefined
}

/**
 * Retrieves data from the specified URL using the HTTP GET method.
 *
 * @param {AxiosInstance} axiosClient - Instance of Axios client.
 * @param {T} url - The URL to retrieve data from.
 * @param {RequestArguments<GetUrls[T]>} args - The arguments for the GET request.
 * @return {Promise<AxiosResponse<TData>>} A Promise that resolves to the data retrieved from the specified URL.
 */
export const getReq = async <T extends keyof GetUrls, TData = RequestResponse<GetUrls[T]>>(
	axiosClient: AxiosInstance,
	url: T,
	args: RequestArgumentsAutoOrg<GetUrls[T]>
): Promise<AxiosResponse<TData>> => {
	const { params, customConfig } = { ...EMPTY_ARGS, ...args }
	const { fullfilURL, requestConfig } = prepareRequestConfig('httpGet', url, params, customConfig)

	const res = await axiosClient.get<TData>(fullfilURL, requestConfig)
	return res
}

/**
 * Performs a POST request to the specified URL with the provided parameters and request body.
 *
 * @param {AxiosInstance} axiosClient - Instance of Axios client.
 * @param {string} url - the URL endpoint for the POST request
 * @param {RequestArguments<PostUrls[T]>} args - The arguments for POST request.
 * @return {Promise<AxiosResponse<RequestResponse<PostUrls[T]>>>} A Promise that resolves to the data retrieved from the specified URL.
 */
export const postReq = async <T extends keyof PostUrls>(
	axiosClient: AxiosInstance,
	url: T,
	args: RequestArgumentsAutoOrg<PostUrls[T]>
): Promise<AxiosResponse<RequestResponse<PostUrls[T]>>> => {
	const { customConfig, params, reqBody } = { ...EMPTY_ARGS, ...args }
	const { fullfilURL, requestConfig } = prepareRequestConfig('httpPost', url, params, customConfig)

	const res = await axiosClient.post<RequestResponse<PostUrls[T]>>(fullfilURL, reqBody, requestConfig)
	showResponseNotifications(res.data, requestConfig.displayNotification)
	return res
}

/**
 * Performs a PUT request to the specified URL with the provided parameters and request body.
 */
export const putReq = async <T extends keyof PutUrls>(
	axiosClient: AxiosInstance,
	url: T,
	args: RequestArgumentsAutoOrg<PutUrls[T]>
): Promise<AxiosResponse<RequestResponse<PutUrls[T]>>> => {
	const { customConfig, params, reqBody } = { ...EMPTY_ARGS, ...args }
	const { fullfilURL, requestConfig } = prepareRequestConfig('httpPut', url, params, customConfig)

	const res = await axiosClient.put<RequestResponse<PutUrls[T]>>(fullfilURL, reqBody, requestConfig)
	showResponseNotifications(res.data, requestConfig.displayNotification)
	return res
}

/**
 * Performs a PATCH request to the specified URL with the provided parameters and request body.
 *
 * @param {AxiosInstance} axiosClient - Instance of Axios client.
 * @param {string} url - the URL endpoint for the PATCH request
 * @param {RequestArguments<PatchUrls[T]>} args - The arguments for PATCH request.
 * @return {Promise<AxiosResponse<RequestResponse<PatchUrls[T]>>>} A Promise that resolves to the data retrieved from the specified URL.
 */
export const patchReq = async <T extends keyof PatchUrls>(
	axiosClient: AxiosInstance,
	url: T,
	args: RequestArgumentsAutoOrg<PatchUrls[T]>
): Promise<AxiosResponse<RequestResponse<PatchUrls[T]>>> => {
	const { customConfig, params, reqBody } = { ...EMPTY_ARGS, ...args }
	const { fullfilURL, requestConfig } = prepareRequestConfig('httpPatch', url, params, customConfig)

	const res = await axiosClient.patch<RequestResponse<PatchUrls[T]>>(fullfilURL, reqBody, requestConfig)
	showResponseNotifications(res.data, requestConfig.displayNotification)
	return res
}

/**
 * Performs a DELETE request to the specified URL with the provided parameters and request body.
 *
 * @param {AxiosInstance} axiosClient - Instance of Axios client.
 * @param {string} url - the URL endpoint for the DELETE request
 * @param {RequestArguments<DeleteUrls[T]>} args - The arguments for DELETE request.
 * @return {Promise<AxiosResponse<RequestResponse<DeleteUrls[T]>>>} A Promise that resolves to the data retrieved from the specified URL.
 */
export const deleteReq = async <T extends keyof DeleteUrls>(
	axiosClient: AxiosInstance,
	url: T,
	args: RequestArgumentsAutoOrg<DeleteUrls[T]>
): Promise<AxiosResponse<RequestResponse<DeleteUrls[T]>>> => {
	const { customConfig, params, reqBody } = { ...EMPTY_ARGS, ...args }
	const { fullfilURL, requestConfig } = prepareRequestConfig('httpDelete', url, params, customConfig)

	const res = await axiosClient.delete<RequestResponse<DeleteUrls[T]>>(fullfilURL, {
		...requestConfig,
		data: reqBody
	})
	showResponseNotifications(res.data, requestConfig.displayNotification)
	return res
}
